import { isNil } from "lodash-es";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { logout } from "@/data/session";
import {
  deleteUser,
  fetchProfilesConnectivityToApplicationsAndContracts,
  fetchUserConsultantProfilesState,
  TCloseAccountCodes,
  TUnableToCloseAccountCodes,
} from "@/data/users";
import { Flex, IDialogModal, inDialogModal } from "@/eworkitComponents";
import { SessionContext } from "@/infrastructure/Session";
import { CloseButton } from "@/views/components/CloseButton";
import { StyledModal } from "@/views/components/Modal";
import { useToasts } from "@/views/components/Toast/useToasts";
import { router } from "@/views/router";

import "./_delete-account.scss";

interface IDeleteModalProps extends IDialogModal {
  onClose: (shouldRefresh?: boolean) => void;
}

interface IModalProps extends IDeleteModalProps {
  onCloseAccount: () => void;
  accountState: TCloseAccountCodes;
  connectivity: boolean;
}

const Modal: React.FC<IModalProps> = ({ onClose, accountState, onCloseAccount, connectivity }) => {
  const { t } = useTranslation("session");
  const unableToCloseAccount = accountState in TUnableToCloseAccountCodes;
  let translationCode = "CONFIRM_DIALOG";

  if (unableToCloseAccount) {
    translationCode = "UNABLE_TO_CLOSE_DIALOG";
  } else if (connectivity) {
    translationCode = "HAS_ACTIVE_APP_CONTRACT";
  }

  const renderButtons = () => {
    return (
      <>
        <Button variant="secondary" onClick={() => onClose()} size="lg" testName="cancel">
          <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CLOSE_ACCOUNT.BUTTON_CANCEL")}</Text>
        </Button>

        {unableToCloseAccount || connectivity ? (
          <Button onClick={() => onClose()} size="lg" testName="understand">
            <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CLOSE_ACCOUNT.UNABLE_TO_CLOSE_DIALOG.BUTTON_APPROVE")}</Text>
          </Button>
        ) : (
          <Button onClick={onCloseAccount} size="lg" testName="delete">
            <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CLOSE_ACCOUNT.CONFIRM_DIALOG.BUTTON_APPROVE")}</Text>
          </Button>
        )}
      </>
    );
  };

  return (
    <StyledModal>
      <Flex direction="column" padding={["lg"]} innerMargin="lg">
        <Flex justify="space-between" innerMargin="sm">
          <Header weight="bold">{t(`USERS.SETTINGS.USER_ACCOUNT.CLOSE_ACCOUNT.${translationCode}.HEADER`)}</Header>
          <CloseButton onClick={() => onClose()} testName="close" />
        </Flex>

        <Flex justify="center">
          <div className="delete__img-container" />
        </Flex>
        <Flex direction="column" innerMargin="md">
          <Text>{t(`USERS.SETTINGS.USER_ACCOUNT.CLOSE_ACCOUNT.${translationCode}.TEXT1`)}</Text>
          <Text>{t(`USERS.SETTINGS.USER_ACCOUNT.CLOSE_ACCOUNT.${translationCode}.TEXT2`)}</Text>
          <Flex justify="flex-end" innerMargin="md">
            {renderButtons()}
          </Flex>
        </Flex>
      </Flex>
    </StyledModal>
  );
};

const CloseAccountModalComponent = inDialogModal(Modal);

export const CloseAccountModal: React.FC<IDeleteModalProps> = React.memo(({ open, onClose }) => {
  const { addToast } = useToasts();
  const User = useContext(UserContext);
  const [accountState, setAccountState] = useState<TCloseAccountCodes>();
  const [connectivityState, setConnectivityState] = useState<boolean>();
  const Session = useContext(SessionContext);
  const history = useHistory();

  const { t } = useTranslation(["session", "common"]);

  useEffect(() => {
    if (open && isNil(accountState)) {
      handleAccountState();
    }
  }, [open]);

  const handleAccountState = useCallback(async () => {
    try {
      const response = await fetchUserConsultantProfilesState();
      setAccountState(
        response.reduce((acc, curr) => {
          return acc.state in TUnableToCloseAccountCodes ? acc : curr.state in TUnableToCloseAccountCodes ? curr : acc;
        }).state,
      );

      const connectivityRes = await fetchProfilesConnectivityToApplicationsAndContracts(
        User.contextConsultantProfileId as number,
      );
      setConnectivityState(connectivityRes.hasActiveAppOrContract);
    } catch (response) {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "warning",
      });
      onClose();
    }
  }, [accountState]);

  const closeAccount = async () => {
    try {
      await deleteUser({ userId: User.sessionUser.id });
      logout();
      Session.destroy(() => {
        history.push(router.get("deleted-account").path);
      });
    } catch (response) {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "warning",
      });
    } finally {
      onClose(true);
    }
  };
  if (open && !isNil(accountState) && !isNil(connectivityState)) {
    return (
      <CloseAccountModalComponent
        onClose={onClose}
        onCloseAccount={closeAccount}
        accountState={accountState!}
        connectivity={connectivityState}
        open={open}
        size="xs"
      />
    );
  }

  return null;
});
